// ─────────────────────────────────────────────────────────────────────────────
// Scribble — shared WebSocket protocol. Single source of truth for both the
// server and the client.
// ─────────────────────────────────────────────────────────────────────────────

export const ROOM_CODE_LENGTH = 6;
export const MIN_PLAYERS = 2;
export const MAX_PLAYERS = 8;

export type PlayerId = string;

export interface GameConfig {
  /** Seconds each drawer has to draw (60 / 90 / 120). */
  turnSeconds: number;
  /** Number of drawing turns per player each game (1 / 2 / 3). */
  roundsPerPlayer: number;
}

export const DEFAULT_CONFIG: GameConfig = {
  turnSeconds: 80,
  roundsPerPlayer: 2,
};

export type Phase = 'lobby' | 'playing' | 'gameover';

/** A snapshot of the draw event log that keeps every client's canvas in sync.
 *  All coordinates are normalized to [0,1] so canvases of any size agree. */
export interface DrawAction {
  kind: 'begin' | 'point' | 'end';
  x: number;
  y: number;
  color: string;
  size: number;
}

export type DrawCommand =
  | { type: 'CLEAR' }
  | { type: 'ACTION'; action: DrawAction };

export interface PlayerPublic {
  id: PlayerId;
  name: string;
  /** 'guest' renders a badge so an unreserved guest label cannot pass for a
   *  registered account with the same name. */
  kind: 'user' | 'guest';
  color: string;
  ready: boolean;
  connected: boolean;
  score: number;
  /** Set once a player guesses the word correctly this round. */
  guessed: boolean;
}

export interface ChatEntry {
  id: string;
  authorId: PlayerId | null; // null for system messages
  authorName: string;
  text: string;
  /** 0 = normal chat, 1 = someone guessed correctly this round. */
  kind: 0 | 1;
}

export interface RoomSnapshot {
  roomCode: string;
  hostId: PlayerId;
  phase: Phase;
  players: PlayerPublic[];
  config: GameConfig;
  currentDrawerId: PlayerId | null;
  /** Number of dots shown to guessers; null when no active round. */
  wordLength: number | null;
  /** Progressive hint: the word with unrevealed letters still masked (`_`),
   *  letters revealed one-by-one as the turn ticks down. Empty when idle. */
  hint: string;
  /** Total rounds this game; null when lobby. */
  totalRounds: number | null;
  /** Rounds elapsed so far (0-based). */
  roundIndex: number;
  turnDeadline: number | null;
  winnerId: PlayerId | null;
  chat: ChatEntry[];
  drawCommands: DrawCommand[];
}

/** Everything a player is allowed to know. Sent alongside most snapshots. */
export interface PrivateState {
  /** Your word if you are the current drawer, else null. */
  yourWord: string | null;
  /** You are the drawer this round. */
  youAreDrawing: boolean;
  /** Your random word for the upcoming round (sniff-able, but only visible
   *  to the person who's about to draw). Optional; used for pre-round peek. */
  upcomingWord: string | null;
}

// ── Client → Server ──────────────────────────────────────────────────────────

// `playerName` is retained for wire compatibility with cached clients but is
// ignored by the server, which takes the name from the signed session cookie.
export type ClientMessage =
  | { type: 'CREATE_ROOM'; playerName?: string }
  | { type: 'JOIN_ROOM'; roomCode: string; playerName?: string }
  | { type: 'RECONNECT'; roomCode: string; playerId: PlayerId; identity: string }
  | { type: 'SET_READY'; ready: boolean }
  | { type: 'START_GAME' }
  | { type: 'SET_CONFIG'; config: Partial<GameConfig> }
  | { type: 'SEND_CHAT'; text: string }
  | { type: 'DRAW'; commands: DrawCommand[] }
  | { type: 'CLEAR_CANVAS' }
  | { type: 'LEAVE_ROOM' };

// ── Server → Client ──────────────────────────────────────────────────────────

export type ServerMessage =
  | { type: 'ERROR'; code: string; message: string }
  | { type: 'ROOM_CREATED'; roomCode: string; playerId: PlayerId; identity: string }
  | { type: 'ROOM_JOINED'; roomCode: string; playerId: PlayerId; identity: string }
  | { type: 'RECONNECTED'; playerId: PlayerId }
  | { type: 'ROOM_SNAPSHOT'; snapshot: RoomSnapshot }
  | { type: 'PRIVATE_STATE'; state: PrivateState }
  | { type: 'GAME_STARTED'; snapshot: RoomSnapshot; state: PrivateState }
  | { type: 'ROUND_STARTED'; snapshot: RoomSnapshot; state: PrivateState }
  | { type: 'ROUND_ENDED'; snapshot: RoomSnapshot }
  | { type: 'GAME_ENDED'; snapshot: RoomSnapshot }
  | { type: 'CHAT'; entry: ChatEntry }
  | { type: 'HINT'; hint: string }
  | { type: 'DRAW'; commands: DrawCommand[] }
  | { type: 'PLAYER_JOINED'; player: PlayerPublic }
  | { type: 'PLAYER_LEFT'; playerId: PlayerId };
// ── Helpers ──────────────────────────────────────────────────────────────────

export function generateRoomCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no confusing 0/O/1/I
  let out = '';
  for (let i = 0; i < ROOM_CODE_LENGTH; i++) {
    out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
}

export function isClientMessage(raw: unknown): raw is ClientMessage {
  return (
    typeof raw === 'object' &&
    raw !== null &&
    typeof (raw as { type?: unknown }).type === 'string'
  );
}